import React, { useEffect, useState } from "react";
import UserListing from "./UserListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setItems,
  setIsTableLoading,
  setTotalItems,
  setIsOpenResetPassword,
} from "src/redux/slices/UserSlice";
import AddUserFormWrapper from "../Add/AddUserFormWrapper";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import EditUserFormWrapper from "../Edit/EditUserFormWrapper";
import { useGetAllUserQuery } from "src/services/UserService";
import { useDeleteUserMutation } from "src/services/UserService";
import { Tooltip } from "@mui/material";
import ResetPasswordFormWrapper from "./ResetPasswordForm/ResetPasswordFormWrapper";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const UserListingWrapper = (props: Props) => {
  const canEditUser = usePermission("USERS", "UPDATE");
  const canDeleteUser = usePermission("USERS", "DELETE");
  const canActiveUser = usePermission("USERS", "ACTIVE/DEACTIVE");
  const dispatch = useDispatch();
  const [userId, setUserId] = useState<any>("");
  const [deleteUser] = useDeleteUserMutation();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isOpenAddDialog,
    isOpenEditDialog,
    isOpenResetPassword,
    isTableLoading,
  } = useSelector((state: RootState) => state.user);

  const { data, isFetching, isLoading } = useGetAllUserQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["name", "email"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  const getActionOptions = (row: any) => {
    const options = [];

    if (canEditUser) {
      options.push({
        label: "Edit",
        onClick: () => {
          dispatch(setIsOpenEditDialog(true));
          setUserId(row?._id);
        },
      });
    }

    if (canDeleteUser) {
      options.push({
        label: "Delete",
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this User?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteUser(row?._id).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", "Deleted successfully");
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
      });
    }

    if (canActiveUser) {
      options.push({
        label: "Reset Password",
        onClick: () => {
          dispatch(setIsOpenResetPassword(true));
          setUserId(row?._id);
        },
      });
    }

    return options;
  };

  const columns: columnTypes[] = [
    { field: "name", headerName: "Name", flex: "flex-[1_1_0%]" },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.email}>
          <div className="capitalize text-md"> {row?.email} </div>
        </Tooltip>
      ),
    },
    { field: "mobile", headerName: "Mobile", flex: "flex-[1_1_0%]" },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <>
      <div>
        <UserListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            isOpenAddDialog,
            isOpenEditDialog,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setIsOpenEditDialog: (value) =>
              dispatch(setIsOpenEditDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>

      {isOpenAddDialog ? (
        <AddUserFormWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}

      {isOpenEditDialog ? (
        <EditUserFormWrapper
          userId={userId}
          onClose={() => dispatch(setIsOpenEditDialog(false))}
        />
      ) : null}

      {isOpenResetPassword ? (
        <ResetPasswordFormWrapper
          userId={userId}
          onClose={() => dispatch(setIsOpenResetPassword(false))}
        />
      ) : null}
    </>
  );
};

export default UserListingWrapper;
